import { supabase } from "@/integrations/supabase/client";
import { HabitType } from "@/types";

export const saveHabitToSupabase = async (habit: HabitType): Promise<boolean> => {
  try {
    console.log('💾 Saving habit to Supabase:', habit.name);

    // Only save actual habits to the habits table, not dailies or todos
    if (habit.type !== 'habit' && habit.type !== 'positive' && habit.type !== 'negative' && habit.type !== 'neutral') {
      console.error('❌ Attempted to save non-habit to habits table:', habit.type);
      return false;
    }

    const habitData = {
      id: habit.id,
      user_id: habit.userId,
      name: habit.name,
      description: habit.description,
      type: habit.positive === false ? 'negative' : 'positive', // Map to valid constraint values
      difficulty: habit.difficulty,
      priority: habit.priority || 'medium',
      value: habit.value || 0,
      frequency: habit.frequency || 'daily',
      points: habit.points || 1,
      streak: habit.streak || 0,
      color: habit.color || '#3B82F6',
      icon: habit.icon,
      tags: habit.tags || [],
      habit_value: habit.habitValue || 0,
      daily_completions: habit.dailyCompletions || 0,
      positive: habit.positive !== false,
      archived: habit.archived || false,
      last_completed_at: habit.lastCompleted?.toISOString(),
    };

    const { error } = await supabase
      .from('habits')
      .upsert(habitData);

    if (error) {
      console.error('❌ Failed to save habit:', error);
      return false;
    }

    console.log('✅ Successfully saved habit to Supabase');
    return true;
  } catch (error) {
    console.error('❌ Error saving habit to Supabase:', error);
    return false;
  }
};

export const saveDailyToSupabase = async (daily: HabitType): Promise<boolean> => {
  try {
    console.log('💾 Saving daily to Supabase:', daily.name);

    const dailyData = {
      id: daily.id,
      user_id: daily.userId,
      name: daily.name,
      description: daily.description,
      difficulty: daily.difficulty,
      priority: daily.priority || 'medium',
      points: daily.points || 1,
      streak: daily.streak || 0,
      color: daily.color || '#10B981',
      icon: daily.icon,
      tags: daily.tags || [],
      completed_today: daily.completedToday || false,
      last_completed_at: daily.lastCompleted?.toISOString(),
    };

    const { error } = await supabase
      .from('dailies')
      .upsert(dailyData);

    if (error) {
      console.error('❌ Failed to save daily:', error);
      return false;
    }

    console.log('✅ Successfully saved daily to Supabase');
    return true;
  } catch (error) {
    console.error('❌ Error saving daily to Supabase:', error);
    return false;
  }
};

export const saveTodoToSupabase = async (todo: HabitType): Promise<boolean> => {
  try {
    console.log('💾 Saving todo to Supabase:', todo.name);

    const todoData = {
      id: todo.id,
      user_id: todo.userId,
      name: todo.name,
      description: todo.description,
      difficulty: todo.difficulty,
      priority: todo.priority || 'medium',
      points: todo.points || 1,
      color: todo.color || '#F59E0B',
      icon: todo.icon,
      tags: todo.tags || [],
      completed: todo.completedToday || false, // preserve current completion state
      due_date: todo.dueDate ? (typeof todo.dueDate === 'string' ? todo.dueDate : todo.dueDate.toISOString()) : null,
      completed_at: null,
    };

    const { error } = await supabase
      .from('todos')
      .upsert(todoData);

    if (error) {
      console.error('❌ Failed to save todo:', error);
      return false;
    }

    console.log('✅ Successfully saved todo to Supabase');
    return true;
  } catch (error) {
    console.error('❌ Error saving todo to Supabase:', error);
    return false;
  }
};

export const markTodoCompleteInSupabase = async (todoId: string): Promise<boolean> => {
  try {
    console.log('✅ Marking todo complete in Supabase:', todoId);

    const { error } = await supabase
      .from('todos')
      .update({
        completed: true,
        completed_at: new Date().toISOString(),
      })
      .eq('id', todoId);

    if (error) {
      console.error('❌ Failed to mark todo complete:', error);
      return false;
    }

    console.log('✅ Successfully marked todo complete in Supabase');
    return true;
  } catch (error) {
    console.error('❌ Error marking todo complete:', error);
    return false;
  }
};

export const markTodoUncompleteInSupabase = async (todoId: string): Promise<boolean> => {
  try {
    console.log('↩️ Marking todo incomplete in Supabase:', todoId);

    const { error } = await supabase
      .from('todos')
      .update({
        completed: false,
        completed_at: null,
      })
      .eq('id', todoId);

    if (error) {
      console.error('❌ Failed to mark todo incomplete:', error);
      return false;
    }

    console.log('✅ Successfully marked todo incomplete in Supabase');
    return true;
  } catch (error) {
    console.error('❌ Error marking todo incomplete:', error);
    return false;
  }
};

export const markDailyCompleteInSupabase = async (dailyId: string): Promise<boolean> => {
  try {
    console.log('✅ Marking daily complete in Supabase:', dailyId);

    const { error } = await supabase
      .from('dailies')
      .update({
        completed_today: true,
        last_completed_at: new Date().toISOString(),
      })
      .eq('id', dailyId);

    if (error) {
      console.error('❌ Failed to mark daily complete:', error);
      return false;
    }

    console.log('✅ Successfully marked daily complete in Supabase');
    return true;
  } catch (error) {
    console.error('❌ Error marking daily complete:', error);
    return false;
  }
};

export const markDailyUncompleteInSupabase = async (dailyId: string): Promise<boolean> => {
  try {
    console.log('🔄 Marking daily uncomplete in Supabase:', dailyId);

    const { error } = await supabase
      .from('dailies')
      .update({
        completed_today: false,
        last_completed_at: null,
      })
      .eq('id', dailyId);

    if (error) {
      console.error('❌ Failed to mark daily uncomplete:', error);
      return false;
    }

    console.log('✅ Successfully marked daily uncomplete in Supabase');
    return true;
  } catch (error) {
    console.error('❌ Error marking daily uncomplete:', error);
    return false;
  }
};

export const logHabitCompletionToSupabase = async (
  habitId: string, 
  userId: string, 
  direction: 1 | -1, 
  pointsEarned: number
): Promise<boolean> => {
  try {
    console.log('📝 Logging habit completion to Supabase:', { habitId, direction, pointsEarned });

    // First check if the habit exists
    const { data: existingHabit, error: checkError } = await supabase
      .from('habits')
      .select('id')
      .eq('id', habitId)
      .single();

    if (checkError || !existingHabit) {
      console.error('❌ Habit not found, cannot log completion:', habitId);
      return false;
    }

    const { error } = await supabase
      .from('habit_completions')
      .insert({
        user_id: userId,
        habit_id: habitId,
        direction,
        points_earned: pointsEarned,
        completed_at: new Date().toISOString(),
      });

    if (error) {
      console.error('❌ Failed to log habit completion:', error);
      return false;
    }

    // Update the habit's last completed time
    const { error: updateError } = await supabase
      .from('habits')
      .update({
        last_completed_at: new Date().toISOString(),
      })
      .eq('id', habitId);

    if (updateError) {
      console.error('❌ Failed to update habit after completion:', updateError);
    }

    console.log('✅ Successfully logged habit completion to Supabase');
    return true;
  } catch (error) {
    console.error('❌ Error logging habit completion:', error);
    return false;
  }
};

export const deleteHabitFromSupabase = async (habitId: string): Promise<boolean> => {
  try {
    console.log('🗑️ Deleting habit from Supabase:', habitId);

    const { error } = await supabase
      .from('habits')
      .delete()
      .eq('id', habitId);

    if (error) {
      console.error('❌ Failed to delete habit:', error);
      return false;
    }

    console.log('✅ Successfully deleted habit from Supabase');
    return true;
  } catch (error) {
    console.error('❌ Error deleting habit from Supabase:', error);
    return false;
  }
};

export const deleteDailyFromSupabase = async (dailyId: string): Promise<boolean> => {
  try {
    console.log('🗑️ Deleting daily from Supabase:', dailyId);

    const { error } = await supabase
      .from('dailies')
      .delete()
      .eq('id', dailyId);

    if (error) {
      console.error('❌ Failed to delete daily:', error);
      return false;
    }

    console.log('✅ Successfully deleted daily from Supabase');
    return true;
  } catch (error) {
    console.error('❌ Error deleting daily from Supabase:', error);
    return false;
  }
};

export const deleteTodoFromSupabase = async (todoId: string): Promise<boolean> => {
  try {
    console.log('🗑️ Deleting todo from Supabase:', todoId);

    const { error } = await supabase
      .from('todos')
      .delete()
      .eq('id', todoId);

    if (error) {
      console.error('❌ Failed to delete todo:', error);
      return false;
    }

    console.log('✅ Successfully deleted todo from Supabase');
    return true;
  } catch (error) {
    console.error('❌ Error deleting todo from Supabase:', error);
    return false;
  }
};